import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import {
  CalendarDays,
  CheckSquare,
  LayoutDashboard,
  ListTodo,
  LogOut,
  MoreHorizontal,
  Plus,
  StickyNote,
  User,
  UserPlus,
  Users,
  MapPin,
} from "lucide-react";
import {
  Translate,
  useCanAccess,
  useGetIdentity,
  useLogout,
  useTranslate,
} from "ra-core";
import { Link, useLocation, useMatch, useNavigate } from "react-router";
import { useMemo, useState, type ComponentType, type SVGProps } from "react";
import { ContactCreateSheet } from "../contacts/ContactCreateSheet";
import { useDeskAttentionCounts } from "../contacts/useDeskAttention";
import { NoteCreateSheet } from "../notes/NoteCreateSheet";
import { TaskCreateSheet } from "../tasks/TaskCreateSheet";
import { ScheduleVisitDrawer } from "../site-visits/ScheduleVisitDrawer";
import {
  filterNavSections,
  isMoreRouteActive,
  isRouteActive,
  navSections,
  type NavSection,
} from "./navConfig";

export const MobileNavigation = () => {
  const location = useLocation();
  const translate = useTranslate();

  const isDashboard = location.pathname === "/";
  const isContacts =
    location.pathname === "/contacts" ||
    location.pathname.startsWith("/contacts/");
  const isSiteVisits =
    location.pathname === "/site_visits" ||
    location.pathname.startsWith("/site_visits/") ||
    location.pathname.startsWith("/site-visits");
  const isMore = isMoreRouteActive(location.pathname);

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/80 bg-background/95 backdrop-blur-md supports-[backdrop-filter]:bg-background/80 pb-[env(safe-area-inset-bottom,0px)]"
    >
      <div className="flex h-14 items-stretch justify-around px-1">
        <NavigationButton
          href="/"
          Icon={LayoutDashboard}
          label={translate("ra.page.dashboard")}
          isActive={isDashboard}
        />
        <NavigationButton
          href="/contacts"
          Icon={Users}
          label={translate("resources.contacts.name", {
            smart_count: 2,
          })}
          isActive={isContacts}
        />
        <CreateButton />
        <NavigationButton
          href="/site_visits"
          Icon={MapPin}
          label="Visits"
          isActive={isSiteVisits}
        />
        <MoreButton isActive={isMore} />
      </div>
    </nav>
  );
};

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button
    asChild
    variant="ghost"
    className={cn(
      "flex-1 flex-col gap-0.5 h-auto min-h-14 py-1.5 px-1 rounded-none max-w-[4.5rem]",
      isActive
        ? "text-primary font-semibold"
        : "text-muted-foreground font-medium",
    )}
  >
    <Link to={href}>
      <Icon
        className={cn(
          "size-5",
          isActive ? "text-primary" : "text-muted-foreground",
        )}
        strokeWidth={isActive ? 2.5 : 2}
      />
      <span className="text-[0.65rem] leading-tight truncate max-w-full">
        {label}
      </span>
    </Link>
  </Button>
);

const CreateButton = () => {
  const translate = useTranslate();
  const contact_id = useMatch("/contacts/:id/*")?.params.id;
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactCreateOpen, setContactCreateOpen] = useState(false);
  const [noteCreateOpen, setNoteCreateOpen] = useState(false);
  const [taskCreateOpen, setTaskCreateOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  return (
    <>
      <ContactCreateSheet
        open={contactCreateOpen}
        onOpenChange={setContactCreateOpen}
      />
      <NoteCreateSheet
        open={noteCreateOpen}
        onOpenChange={setNoteCreateOpen}
        contact_id={contact_id}
      />
      <TaskCreateSheet
        open={taskCreateOpen}
        onOpenChange={setTaskCreateOpen}
        contact_id={contact_id}
      />
      <ScheduleVisitDrawer
        isOpen={scheduleOpen}
        onOpenChange={setScheduleOpen}
        contactId={contact_id}
      />

      <div className="flex items-center justify-center px-1 -mt-3">
        <Button
          variant="default"
          size="icon"
          className="h-12 w-12 rounded-full shadow-md ring-4 ring-background"
          aria-label={translate("ra.action.create")}
          onClick={() => setMenuOpen(true)}
        >
          <Plus className="size-6" />
        </Button>
      </div>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-8 pt-2">
          <SheetHeader className="pb-2">
            <SheetTitle className="text-base">Create</SheetTitle>
          </SheetHeader>
          <div className="grid gap-1">
            <CreateAction
              icon={UserPlus}
              label={translate("resources.contacts.forcedCaseName")}
              onSelect={() => {
                setMenuOpen(false);
                setContactCreateOpen(true);
              }}
            />
            <CreateAction
              icon={CalendarDays}
              label="Schedule Site Visit"
              onSelect={() => {
                setMenuOpen(false);
                setScheduleOpen(true);
              }}
            />
            <CreateAction
              icon={CheckSquare}
              label={translate("resources.tasks.forcedCaseName")}
              onSelect={() => {
                setMenuOpen(false);
                setTaskCreateOpen(true);
              }}
            />
            {contact_id ? (
              <CreateAction
                icon={StickyNote}
                label={translate("resources.notes.forcedCaseName")}
                onSelect={() => {
                  setMenuOpen(false);
                  setNoteCreateOpen(true);
                }}
              />
            ) : null}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const CreateAction = ({
  icon: Icon,
  label,
  onSelect,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  onSelect: () => void;
}) => (
  <button
    type="button"
    className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm font-medium hover:bg-muted active:bg-muted/80"
    onClick={onSelect}
  >
    <Icon className="size-5 text-primary shrink-0" />
    <span>{label}</span>
  </button>
);

const MoreButton = ({ isActive }: { isActive: boolean }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const translate = useTranslate();
  const logout = useLogout();
  const { data: identity } = useGetIdentity();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "list",
  });

  const sections: NavSection[] = useMemo(() => {
    const withTasks = navSections.map((section) => {
      if (section.title !== "Sales & Pipeline") return section;
      return {
        ...section,
        items: [
          ...section.items,
          {
            label: translate("resources.tasks.name", { smart_count: 2 }),
            path: "/tasks",
            icon: ListTodo,
          },
        ],
      };
    });
    return filterNavSections(withTasks, Boolean(isAdmin));
  }, [translate, isAdmin]);
  const { needsWhatsAppReply: inboxCount } = useDeskAttentionCounts();

  const displayName = identity?.fullName?.trim() || "Account";
  const initials = displayName.charAt(0).toUpperCase() || "U";

  return (
    <>
      <Button
        variant="ghost"
        className={cn(
          "flex-1 flex-col gap-0.5 h-auto min-h-14 py-1.5 px-1 rounded-none max-w-[4.5rem]",
          isActive
            ? "text-primary font-semibold"
            : "text-muted-foreground font-medium",
        )}
        aria-label="More"
        onClick={() => setOpen(true)}
      >
        <MoreHorizontal
          className={cn(
            "size-5",
            isActive ? "text-primary" : "text-muted-foreground",
          )}
          strokeWidth={isActive ? 2.5 : 2}
        />
        <span className="text-[0.65rem] leading-tight">More</span>
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="h-[85vh] max-h-[85vh] rounded-t-2xl p-0 gap-0 flex flex-col"
        >
          <SheetHeader className="px-4 pt-4 pb-2 border-b shrink-0">
            <SheetTitle className="text-base">Menu</SheetTitle>
          </SheetHeader>
          <div className="overflow-y-auto flex-1 px-3 py-3 space-y-4">
            {sections.map((section) => (
              <div key={section.title} className="space-y-1">
                <h4 className="px-3 text-[10px] font-bold text-muted-foreground tracking-wider uppercase pt-1">
                  {section.title}
                </h4>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isRouteActive(location.pathname, item.path);
                  return (
                    <button
                      key={item.path}
                      type="button"
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors",
                        active
                          ? "bg-primary/10 text-primary font-semibold"
                          : "text-foreground font-medium hover:bg-muted",
                      )}
                      onClick={() => {
                        setOpen(false);
                        navigate(item.path);
                      }}
                    >
                      <Icon
                        className={cn(
                          "size-4 shrink-0",
                          active ? "text-primary" : "text-muted-foreground",
                        )}
                      />
                      <span className="truncate flex-1 text-left">
                        {item.label}
                      </span>
                      {item.path === "/inbox" && inboxCount > 0 ? (
                        <Badge className="h-4 min-w-4 px-1 rounded-full bg-red-600 text-white text-[9px] leading-none flex items-center justify-center">
                          {inboxCount > 99 ? "99+" : inboxCount}
                        </Badge>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="shrink-0 border-t bg-muted/30 px-3 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] space-y-1">
            <div className="flex items-center gap-3 px-3 py-2">
              <Avatar className="h-9 w-9">
                <AvatarImage src={identity?.avatar} role="presentation" />
                <AvatarFallback className="text-sm font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{displayName}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {translate("crm.profile.title", { _: "Profile" })}
                </p>
              </div>
            </div>
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium hover:bg-muted"
              onClick={() => {
                setOpen(false);
                navigate("/profile");
              }}
            >
              <User className="size-4 text-muted-foreground shrink-0" />
              <span>{translate("crm.profile.title", { _: "Profile" })}</span>
            </button>
            <button
              type="button"
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/10"
              onClick={() => {
                setOpen(false);
                void logout();
              }}
            >
              <LogOut className="size-4 shrink-0" />
              <Translate i18nKey="ra.auth.logout">Log out</Translate>
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
